import { useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  assemblyPctNum,
  DEFAULT_DASHBOARD_BOXES,
  DEPARTMENTS,
  mesHolds,
  mesOverdue,
  modelKey,
  MODEL_ORDER,
  parseDeliveryDate,
  type OverviewVessel,
  type Permission,
} from '@technohull/shared';
import { useAgendaUpcoming, useNotifications, useNotificationsByDepartment, useOverview, useReport, useServiceRequests } from '../features/hooks.js';
import { startOfTodayUtc, useFleetPlacement, type PlacedBoat } from '../features/fleetPlacement.js';
import { useOrders } from '../features/orders.js';
import { useAuth } from '../auth/AuthContext.js';
import { EmptyState, Fact, FactEnd, FactSentence, InlineLoading, LogoSpin, PageHeader } from '../components/ui.js';
import { IconAlertTriangle, IconCheck, IconPause, IconRefresh } from '../components/icons.js';
import { JourneyLegend, JourneyStrip, journeyStateWord } from '../components/JourneyStrip.js';
import { useDateFormatter, useFormatDate } from '../lib/format.js';
import { localTodayIso } from '../lib/dates.js';
import { MQ, useMediaQuery } from '../lib/media.js';
import { useSeason } from '../season/SeasonContext.js';

// The "Evolved" dashboard: the season written as a sentence with status-coloured figures, "Where the
// fleet is" (one tick per boat at its current stage), the lists people act on (overdue, on hold, this
// week, upcoming), then the statistics. Every boat row reads SAP · model · name · journey strip · date
// or %. Styles: the "Dashboard" section of styles/global.css.

const DAY = 86_400_000;
const AGENDA_CAP = 6;
const addDaysIso = (iso: string, n: number) => {
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
// "today" as UTC midnight of the viewer's calendar day (the Assembly Board's own), so day counts are
// date-to-date and both pages call the same boats overdue. Panels re-take it on each Overview load.
const utcToday = () => +startOfTodayUtc();
const plural = (n: number, one: string, many = `${one}s`) => (n === 1 ? one : many);
const rankModel = (m: string) => { const i = (MODEL_ORDER as readonly string[]).indexOf(m); return i < 0 ? 99 : i; };
/** "Wed 7 Oct" in the user's region. Delivery and agenda dates are calendar dates, so read them as UTC. */
/** a delivery date relative to today: "in 6 days", "tomorrow", "yesterday", "4 days late". */
const relDays = (days: number) =>
  days < -1 ? `${-days} days late` : days === -1 ? 'yesterday' : days === 0 ? 'today' : days === 1 ? 'tomorrow' : `in ${days} days`;

/** boats on the floor (not left, not delivered) past their delivery date, longest overdue first: the
 *  Assembly Board's overdue (mesOverdue on the boats on the floor). */
function overdueOf(vessels: OverviewVessel[], today: number) {
  const now = new Date(today);
  return onFloor(vessels)
    .filter((v) => mesOverdue(v, now))
    .map((v) => ({ v, d: parseDeliveryDate(v) }))
    .filter((x): x is { v: OverviewVessel; d: Date } => !!x.d)
    .sort((a, b) => a.d.getTime() - b.d.getTime());
}
/** boats still in the yard with a delivery date from today up to `days` ahead. */
const dueWithin = (vessels: OverviewVessel[], today: number, days: number) =>
  vessels.filter((v) => { if (v.left) return false; const d = parseDeliveryDate(v); return !!d && d.getTime() >= today && d.getTime() <= today + days * DAY; }).length;
/** on the floor = not left and not delivered (the assembly board's definition). */
const onFloor = (vessels: OverviewVessel[]) => vessels.filter((v) => !v.left && !v.deliveryConfirmed);
const avgAssembly = (active: OverviewVessel[]) =>
  active.length ? Math.round(active.reduce((a, v) => a + assemblyPctNum(v), 0) / active.length) : null;

// On hold is the Assembly Board's: mesHolds (every Yes/Hold status column, any case) on the boats on the
// floor. Its labels in words, the four that hold the boat at a build stage first (in build order), then
// the finishing tasks. A label not listed here still shows, as a task, under its own name.
const HOLD_WORDS: Array<[string, string, 'stage' | 'task']> = [
  ['Eng. Install', 'Engine install', 'stage'], ['Eng. Start-up', 'Start-up', 'stage'], ['Sea Trial', 'Sea trial', 'stage'], ['PDI', 'PDI', 'stage'],
  ['UH', 'Upholstery', 'task'], ['Stickers', 'Stickers', 'task'], ['WA', 'Wash', 'task'], ['POL', 'Polish', 'task'],
  ['FW', 'FW', 'task'], ['FRL', 'FRL', 'task'], ['AnF', 'AnF', 'task'],
];
const holdRank = (l: string) => { const i = HOLD_WORDS.findIndex(([k]) => k === l); return i < 0 ? HOLD_WORDS.length : i; };
const holdsOf = (v: OverviewVessel): Array<[string, string, 'stage' | 'task']> =>
  mesHolds(v)
    .sort((a, b) => holdRank(a) - holdRank(b))
    .map((l) => HOLD_WORDS.find(([k]) => k === l) ?? [l, l, 'task']);
/** boats on the floor with any status on hold. */
const heldOf = (vessels: OverviewVessel[]) => onFloor(vessels).filter((v) => mesHolds(v).length > 0);

/** scroll to a panel further down this page and move focus to it, without putting a hash in the URL
 *  (so Back still leaves the page). .content's own scroll-behavior handles smooth vs instant. Returns
 *  undefined when the panel is not on the page, so the figure renders as plain text. */
const jumpTo = (id: string | undefined) => (id ? () => {
  const el = document.getElementById(id);
  el?.scrollIntoView({ block: 'start' });
  el?.focus({ preventScroll: true });
} : undefined);

// two glyphs the shared icon set lacks, drawn in its stroke (24 grid, 1.9 stroke, round caps)
const svgProps = { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.9, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true };
const IconCalendar = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} {...svgProps}><path d="M4 6h16v14H4zM4 10.5h16M8.5 3.5v4M15.5 3.5v4" /></svg>
);
const IconArrowRight = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} {...svgProps}><path d="M5 12h14M13 6l6 6-6 6" /></svg>
);

// ── building blocks ─────────────────────────────────────────────────────────────
function Panel({ id, title, sub, extra, to, linkText, className, children }: {
  id: string; title: string; sub?: ReactNode; extra?: ReactNode; to?: string; linkText?: string; className?: string; children: ReactNode;
}) {
  return (
    <section className={`card dash-pnl${className ? ` ${className}` : ''}`} id={id} tabIndex={-1} aria-labelledby={`${id}-h`}>
      <div className="dash-pnl-h">
        <h2 id={`${id}-h`}>{title}</h2>
        {sub != null && sub !== '' && <span className="dash-n">{sub}</span>}
        {extra}
        {to && <Link to={to} className="dash-lk">{linkText}<IconArrowRight /></Link>}
      </div>
      {children}
    </section>
  );
}
/** a panel's line while it has no data yet, so it never claims "nothing" before (or instead of) loading. */
const Pending = ({ isError }: { isError: boolean }) => (
  isError ? <p className="dash-empty" role="alert">Could not load.</p> : <InlineLoading className="dash-empty" />
);
const Empty = ({ children }: { children: ReactNode }) => <p className="dash-empty">{children}</p>;

/** a boat's name, opening the Overview filtered to its SAP (the top-bar search's hand-off). */
function BoatName({ v, open }: { v: OverviewVessel; open: boolean }) {
  const name = v.name || 'Unnamed boat';
  if (!open || !v.sap) return <span className="dash-cust">{name}</span>;
  return <Link className="dash-cust" to={`/overview?q=${encodeURIComponent(v.sap)}`} title={`Open ${v.sap} in the Overview`}>{name}</Link>;
}
const ModelChip = ({ v }: { v: OverviewVessel }) => {
  const key = v.model ? modelKey(v.model) : '-';
  return <span className="dash-mdl" title={v.model && v.model !== key ? v.model : undefined}>{key || '-'}</span>;
};
const Pct = ({ v }: { v: OverviewVessel }) => <span className="dash-pct" title="Assembly complete">{Math.round(assemblyPctNum(v))}%</span>;

// ── the season as a sentence (the "glance" box, overview:read) ──────────────────
interface FactTargets { overdue?: string; holds?: string; deliveries?: string; completion?: string; boats?: string }
function SeasonFacts({ season, targets }: { season: string; targets: FactTargets }) {
  const { data, dataUpdatedAt, isError } = useOverview();
  const f = useMemo(() => {
    if (!data) return null;
    const today = utcToday();
    const vessels = data.vessels;
    return {
      total: vessels.length,
      overdue: overdueOf(vessels, today).length,
      holds: heldOf(vessels).length,
      due30: dueWithin(vessels, today, 30),
      avg: avgAssembly(onFloor(vessels)),
    };
  }, [data, dataUpdatedAt]); // eslint-disable-line react-hooks/exhaustive-deps
  const lead = season ? `Season ${season} has` : 'This season has';
  if (!f) {
    return (
      <div className="dash-wide" role={isError ? 'alert' : 'status'}>
        <FactSentence className="dash-wait">{isError ? 'Could not load the season figures.' : `${lead} …`}</FactSentence>
      </div>
    );
  }
  return (
    <FactSentence className="dash-wide">
      {lead}{' '}
      <Fact to={targets.boats}>{f.total} {plural(f.total, 'boat')}</Fact><FactEnd>,</FactEnd>{' '}
      {f.overdue
        ? <Fact tone="err" icon={<IconAlertTriangle />} onClick={jumpTo(targets.overdue)}>{f.overdue} {plural(f.overdue, 'delivery', 'deliveries')} overdue</Fact>
        : <Fact tone="ok" icon={<IconCheck />}>no deliveries overdue</Fact>}
      <FactEnd>,</FactEnd>{' '}
      {f.holds
        ? <Fact tone="warn" icon={<IconPause />} onClick={jumpTo(targets.holds)}>{f.holds} {plural(f.holds, 'boat')} on hold</Fact>
        : <Fact tone="ok" icon={<IconCheck />}>no boats on hold</Fact>}{' '}
      and{' '}
      {f.due30
        ? <Fact tone="info" icon={<IconCalendar />} onClick={jumpTo(targets.deliveries)}>{f.due30} due in the next 30 days</Fact>
        : <Fact>none due in the next 30 days</Fact>}
      <FactEnd>.</FactEnd>{' '}
      {f.avg != null
        ? <>Assembly averages <Fact tone="acc" onClick={jumpTo(targets.completion)}>{f.avg}%</Fact><FactEnd>.</FactEnd></>
        : <>No boats are on the floor.</>}
    </FactSentence>
  );
}

// ── where the fleet is: one tick per boat at its current stage (the "fleet" box) ─
// The placement is features/fleetPlacement (shared with the Assembly Board's stage load): idle boats
// (GRP sent them over, assembly not started) are a low tick at Assembly plus "+N idle", Delivery is
// "ready for delivery" (PDI signed off) and delivered boats are noted beside it as "+N delivered".
const STAGE_SHORT: Record<string, string> = { grp: 'GRP', assembly: 'Asm', engine: 'Eng', startup: 'Start', seatrial: 'Trial', pdi: 'PDI', delivery: 'Dlv' };
const TICK_ORDER: Record<string, number> = { current: 0, hold: 1, late: 2 };
const tickWord = (b: PlacedBoat, i: number) =>
  b.at === 'delivery' && b.state === 'current' ? 'ready for delivery' : journeyStateWord(b.steps[i]!, i);

function FleetStages() {
  const { placement: fleet, isError } = useFleetPlacement(true);
  const sub = `one tick per boat${fleet?.notStarted.length ? ` · ${fleet.notStarted.length} not started yet` : ''}`;
  return (
    <Panel id="dash-fleet" className="dash-wide dash-fleet" title="Where the fleet is" sub={sub} extra={<JourneyLegend className="dash-key" />}>
      {!fleet ? <Pending isError={isError} /> : (
        <ol className="dash-load">
          {fleet.stages.map((s, i) => {
            const ticks = [...s.boats].sort((a, b) => (TICK_ORDER[a.state] ?? 0) - (TICK_ORDER[b.state] ?? 0));
            const n = ticks.length;
            const idle = s.key === 'assembly' ? fleet.idle : [];
            const delivered = s.key === 'delivery' ? fleet.delivered.length : 0;
            const late = ticks.filter((t) => t.state === 'late').length;
            const hold = ticks.filter((t) => t.state === 'hold').length;
            const notes = [
              late && `${late} ${s.key === 'delivery' ? 'overdue' : 'over target'}`,
              hold && `${hold} on hold`,
              idle.length && `plus ${idle.length} idle, waiting to start`,
              delivered && `${delivered} delivered`,
            ].filter(Boolean).join(', ');
            return (
              <li key={s.key} className="dash-ls">
                <div className="dash-ls-t">
                  <span className="dash-ls-n"><span className="lg">{s.label}</span><span className="sh" aria-hidden="true">{STAGE_SHORT[s.key] ?? s.label}</span></span>
                  <b className={`dash-ls-c${n ? '' : ' z'}`} aria-hidden="true">{n}</b>
                  <span className="sr-only">: {s.key === 'delivery' ? `${n} ready for delivery` : `${n} ${plural(n, 'boat')}`}{notes ? `, ${notes}` : ''}</span>
                </div>
                <span className="dash-lb" aria-hidden="true">
                  {ticks.map((t) => <i key={t.v.rowId} className="journey-seg" data-state={t.state} title={`${t.v.sap} · ${t.v.name || 'Unnamed boat'} · ${tickWord(t, i)}`} />)}
                  {idle.map((t) => <i key={t.v.rowId} className="journey-seg" data-state="todo" title={`${t.v.sap} · ${t.v.name || 'Unnamed boat'} · idle ${t.days}d, assembly not started`} />)}
                  {n === 0 && idle.length === 0 && <i className="journey-seg" data-state="todo" />}
                </span>
                {idle.length > 0 && <span className="dash-ls-x is-idle" aria-hidden="true">+{idle.length} idle</span>}
                {delivered > 0 && <span className="dash-ls-x is-dlv" aria-hidden="true">+{delivered} delivered</span>}
              </li>
            );
          })}
        </ol>
      )}
    </Panel>
  );
}

// ── the lists people act on ─────────────────────────────────────────────────────
function OverdueDeliveries() {
  const { data, dataUpdatedAt, isError } = useOverview();
  const { canSeePath } = useAuth();
  const fmtDate = useFormatDate();
  const df = useDateFormatter();
  const today = useMemo(utcToday, [dataUpdatedAt]); // eslint-disable-line react-hooks/exhaustive-deps
  const all = useMemo(() => overdueOf(data?.vessels ?? [], today), [data, today]);
  const rows = all.slice(0, 10);
  const open = canSeePath('/overview');
  const sub = all.length > rows.length ? `${rows.length} of ${all.length}` : all.length || undefined;
  return (
    <Panel id="dash-overdue" title="Overdue deliveries" sub={data ? sub : undefined} to="/assembly-alerts" linkText="Alerts">
      {!data ? <Pending isError={isError} /> : all.length === 0 ? <Empty>Nothing overdue. A boat shows here once its delivery date passes and it has not been delivered or left the yard.</Empty> : (
        <ul className="dash-rows">
          {rows.map(({ v, d }) => {
            const days = Math.round((today - d.getTime()) / DAY);
            return (
              <li key={v.rowId} className="dash-row dash-boat dash-r-over">
                <span className="dash-sap">{v.sap || '-'}</span>
                <ModelChip v={v} />
                <BoatName v={v} open={open} />
                <JourneyStrip vessel={v} />
                <span className="dash-due" title={fmtDate(v.deliveryDate)}><span>{df.date(d, { weekday: 'short', year: 'auto' })}</span><small className="dash-late">{relDays(-days)}</small></span>
                <Pct v={v} />
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}

// boats on the floor with a status column on hold on the Overview (the Assembly Board's on hold). There
// is no release action here: a hold is cleared on the Overview (or the boat's own page), which each name opens.
function BoatsOnHold() {
  const { data, isError } = useOverview();
  const { canSeePath } = useAuth();
  const all = useMemo(() => heldOf(data?.vessels ?? []), [data]);
  const rows = all.slice(0, 10);
  const open = canSeePath('/overview');
  const atStage = all.filter((v) => holdsOf(v).some(([, , kind]) => kind === 'stage')).length;
  const taskOnly = all.length - atStage;
  const sub = all.length
    ? [atStage && `${atStage} ${plural(atStage, 'boat')} at a stage`, taskOnly && `${taskOnly} task ${plural(taskOnly, 'hold')}`].filter(Boolean).join(' · ')
    : undefined;
  return (
    <Panel id="dash-holds" title="On hold" sub={data ? sub : undefined} to="/overview" linkText="Overview">
      {!data ? <Pending isError={isError} /> : all.length === 0 ? <Empty>No boats on hold. A boat on the floor shows here when any of its statuses is set to Hold on the Overview.</Empty> : (
        <ul className="dash-rows">
          {rows.map((v) => {
            const h = holdsOf(v);
            const first = h[0]?.[1] ?? 'Hold';
            return (
              <li key={v.rowId} className="dash-row dash-boat dash-r-hold">
                <span className="dash-sap">{v.sap || '-'}</span>
                <ModelChip v={v} />
                <BoatName v={v} open={open} />
                <JourneyStrip vessel={v} />
                <span className="pill warn dash-hold" title={`On hold: ${h.map(([, l]) => l).join(', ')}`}>
                  <IconPause size={12} />{first} hold{h.length > 1 ? ` +${h.length - 1}` : ''}
                </span>
                <Pct v={v} />
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}

function WeekAgenda() {
  const today = localTodayIso();
  const to = useMemo(() => addDaysIso(today, 6), [today]); // rolling 7-day window (today + next 6)
  const { data, isError } = useAgendaUpcoming(today, to);
  const df = useDateFormatter();
  const all = useMemo(() => {
    const list = [...(data?.entries ?? [])];
    list.sort((a, b) => (a.date === b.date ? (a.time || '99:99').localeCompare(b.time || '99:99') : a.date.localeCompare(b.date)));
    return list;
  }, [data]);
  // the next six: this panel shares a row with Overdue deliveries, so it stays a glance; the Agenda has the rest
  const entries = all.slice(0, AGENDA_CAP);
  const tomorrow = addDaysIso(today, 1);
  // a calendar tile: weekday + day number, with the month under it
  const tile = (iso: string) => `${df.weekday(iso)} ${Number(iso.slice(8))}`;
  return (
    <Panel id="dash-agenda" title="This week" sub={all.length > entries.length ? `next ${entries.length} of ${all.length}` : undefined} to="/agenda" linkText="Agenda">
      {!data ? <Pending isError={isError} /> : entries.length === 0 ? <Empty>Nothing on the agenda for the next seven days.</Empty> : (
        <ul className="dash-rows">
          {entries.map((e) => {
            const isToday = e.date <= today;
            const rel = isToday ? 'Today' : e.date === tomorrow ? 'Tomorrow' : df.month(e.date, { year: false });
            const time = e.time ? (e.endTime ? `${e.time}–${e.endTime}` : e.time) : '';
            return (
              <li key={e.id} className={`dash-row dash-r-wk${e.done ? ' is-done' : ''}`}>
                <span className="dash-dt"><b>{tile(isToday ? today : e.date)}</b><small className={isToday ? 'is-today' : undefined}>{rel}</small></span>
                <span className="dash-time">{time}</span>
                <span className="dash-txt">
                  {e.done && <span className="sr-only">Done: </span>}{e.title}
                  {e.endDate && e.endDate > e.date && <small>until {df.date(e.endDate, { weekday: 'short', year: 'auto' })}</small>}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}

// boats that haven't LEFT the yard with a delivery date in the next 30 days, soonest first.
// Confirmed-but-not-departed boats are upcoming deliveries too. Overdue boats (overdueOf: past date, not
// delivered) have their own panel; when that one is switched off they lead this list, so they are never
// dropped from the page. "All N" opens the Overview, which carries every boat's delivery date.
function UpcomingDeliveries({ withOverdue }: { withOverdue: boolean }) {
  const { data, dataUpdatedAt, isError } = useOverview();
  const { canSeePath } = useAuth();
  const fmtDate = useFormatDate();
  const df = useDateFormatter();
  const today = useMemo(utcToday, [dataUpdatedAt]); // eslint-disable-line react-hooks/exhaustive-deps
  const { all, rows } = useMemo(() => {
    const dated = (data?.vessels ?? [])
      .filter((v) => !v.left)
      .map((v) => ({ v, d: parseDeliveryDate(v) }))
      .filter((x): x is { v: OverviewVessel; d: Date } => !!x.d && (x.d.getTime() >= today || (withOverdue && !x.v.deliveryConfirmed)))
      .sort((a, b) => a.d.getTime() - b.d.getTime());
    const horizon = today + 30 * DAY;
    return { all: dated.length, rows: dated.filter((x) => x.d.getTime() <= horizon).slice(0, 10) };
  }, [data, today, withOverdue]);
  const due30 = useMemo(() => dueWithin(data?.vessels ?? [], today, 30), [data, today]);
  const open = canSeePath('/overview');
  return (
    <Panel id="dash-deliveries" title="Upcoming deliveries" sub={data ? `${due30 || 'none'} in the next 30 days` : undefined}
      to={data && all ? (open ? '/overview' : '/assembly-alerts') : undefined} linkText={`All ${all}`}>
      {!data ? <Pending isError={isError} /> : rows.length === 0 ? (
        <Empty>{all ? 'Nothing due in the next 30 days.' : 'No delivery dates set. Boats appear here once the Overview has a delivery date for them.'}</Empty>
      ) : (
        <ul className="dash-rows">
          {rows.map(({ v, d }) => {
            const days = Math.round((d.getTime() - today) / DAY);
            const tone = days < 0 ? 'dash-late' : days <= 3 ? 'is-soon' : undefined;
            return (
              <li key={v.rowId} className="dash-row dash-boat dash-r-up">
                <span className="dash-dt" title={fmtDate(v.deliveryDate)}><b>{df.date(d, { weekday: 'short', year: 'auto' })}</b><small className={tone}>{relDays(days)}</small></span>
                <span className="dash-who">
                  <BoatName v={v} open={open} />
                  <small><span className="dash-sap">{v.sap || '-'}</span><ModelChip v={v} /></small>
                </span>
                <JourneyStrip vessel={v} />
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}

function MyOrders({ email }: { email: string }) {
  const { data, isError } = useOrders();
  const mine = useMemo(() => {
    const orders = data?.orders ?? [];
    const own = orders.filter((o) => o.createdBy === email);
    return (own.length ? own : orders).slice(0, 6);
  }, [data, email]);
  const pill = (s: string) => (s === 'draft' ? 'info' : s === 'confirmed' || s === 'completed' ? 'ok' : 'warn');
  return (
    <Panel id="dash-orders" title="Recent orders" to="/sales/orders" linkText="Sales orders">
      {!data ? <Pending isError={isError} /> : mine.length === 0 ? <Empty>No orders yet.</Empty> : (
        <ul className="dash-rows">
          {mine.map((o) => (
            <li key={o.id} className="dash-row dash-r-simple">
              <Link className="dash-cust" to={`/configurator?id=${o.id}`}>{o.vesselName || o.customer || o.orderNumber}</Link>
              <span className={`pill ${pill(o.status)}`}>{o.status}</span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

// client service bookings waiting for a decision.
function OpenServiceRequests() {
  const { data, isError } = useServiceRequests();
  const requests = data?.requests ?? [];
  const news = requests.filter((r) => r.status === 'new');
  const rows = (news.length ? news : requests).slice(0, 6);
  // the Service Requests page's own tones: new is info, approved ok, declined bad
  const pill = (s: string) => (s === 'new' ? 'info' : s === 'approved' ? 'ok' : s === 'declined' ? 'bad' : '');
  return (
    <Panel id="dash-servicereq" title="Service requests" sub={data?.newCount ? `${data.newCount} new` : undefined} to="/service-requests" linkText="Service requests">
      {!data ? <Pending isError={isError} /> : rows.length === 0 ? <Empty>No service requests.</Empty> : (
        <ul className="dash-rows">
          {rows.map((r) => (
            <li key={r.ref} className="dash-row dash-r-simple">
              <span className="dash-cust">{r.vesselName} <span className="dash-meta">· {r.clientName}</span></span>
              <span className={`pill ${pill(r.status)}`}>{r.status}</span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function NotificationsCard() {
  const { data, isError } = useNotifications();
  const fmtDate = useFormatDate();
  const items = (data?.notifications ?? []).slice(0, 6);
  return (
    <Panel id="dash-notifications" title="Notifications" sub={data?.unread ? `${data.unread} new` : undefined}>
      {!data ? <Pending isError={isError} /> : items.length === 0 ? <Empty>No notifications.</Empty> : (
        <ul className="dash-rows">
          {items.map((n) => (
            <li key={n.id} className={`dash-row dash-r-simple${n.read_at ? ' is-read' : ''}`}>
              <span className="dash-cust">{!n.read_at && <><i className="dash-dot" aria-hidden="true" /><span className="sr-only">Unread: </span></>}{n.title}</span>
              <span className="dash-meta">{fmtDate(n.created_at)}</span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

// recent notification activity grouped by department (management view).
function NotificationsByDept() {
  const { data, isError } = useNotificationsByDepartment();
  const rows = data?.rows ?? [];
  const label = (id: string) => DEPARTMENTS.find((d) => d.id === id)?.label ?? id;
  return (
    <Panel id="dash-notif-dept" title="Notifications by department" sub="last 30 days">
      {!data ? <Pending isError={isError} /> : rows.length === 0 ? <Empty>No notifications in the last 30 days.</Empty> : (
        <ul className="dash-rows">
          {rows.map((r) => (
            <li key={r.department} className="dash-row dash-r-simple">
              <span className="dash-cust">{label(r.department)}</span>
              <span className="dash-meta">{r.total}{r.unread ? ` · ${r.unread} unread` : ''}</span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

// ── statistics ──────────────────────────────────────────────────────────────────
/** a model's bar: label, meter, value. `avg` draws the dashed average marker across the meter. */
function ModelBar({ model, pct, value, title, avg }: { model: string; pct: number; value: string; title: string; avg?: number | null }) {
  const w = Math.max(0, Math.min(100, Math.round(pct)));
  const style = { '--w': `${w}%`, ...(avg != null ? { '--avg': `${Math.max(0, Math.min(100, avg))}%` } : {}) } as CSSProperties;
  return (
    <div className="dash-mb" title={title}>
      <b>{model}</b>
      <span className={`dash-mt${w >= 100 ? ' full' : ''}${avg != null ? ' has-avg' : ''}`} style={style}><i /></span>
      <span className="dash-mv">{value}</span>
      <span className="sr-only">{title}</span>
    </div>
  );
}
const modelGrid = (n: number) => ({ '--rows': Math.max(1, Math.ceil(n / 3)) }) as CSSProperties;

// average assembly completion per model, across the boats still on the floor (the same set and pct
// source as "Assembly averages" in the sentence above).
function CompletionByModel() {
  const { data, isError } = useOverview();
  const { rows, avg } = useMemo(() => {
    const active = onFloor(data?.vessels ?? []);
    const acc = new Map<string, { sum: number; n: number }>();
    for (const v of active) {
      const k = modelKey(v.model);
      const cur = acc.get(k) ?? { sum: 0, n: 0 };
      cur.sum += assemblyPctNum(v);
      cur.n += 1;
      acc.set(k, cur);
    }
    const list = [...acc.entries()]
      .map(([model, a]) => ({ model, avg: Math.round(a.sum / a.n), n: a.n }))
      .sort((a, b) => rankModel(a.model) - rankModel(b.model) || a.model.localeCompare(b.model));
    return { rows: list, avg: avgAssembly(active) };
  }, [data]);
  return (
    <Panel id="dash-completion" className="dash-wide" title="Assembly completion by model" to="/overview" linkText="Overview"
      sub={data && avg != null ? <span className="dash-avgk">average {avg}%</span> : undefined}>
      {!data ? <Pending isError={isError} /> : rows.length === 0 ? <Empty>No boats on the floor.</Empty> : (
        <div className="dash-models" style={modelGrid(rows.length)}>
          {rows.map((r) => (
            <ModelBar key={r.model} model={r.model} pct={r.avg} value={`${r.avg}%`} avg={avg}
              title={`${r.model}: ${r.avg}% average across ${r.n} ${plural(r.n, 'boat')}`} />
          ))}
        </div>
      )}
    </Panel>
  );
}

// how many boats each model needs, from the Assembly Reporting plan (the "boat settings" goals):
// planned count per model vs how many exist, and how many are still needed.
function BuildPlan() {
  const { data, isError } = useReport();
  const rows = useMemo(() => {
    if (!data) return [];
    const planned = data.plannedOrders ?? {};
    const have = new Map(data.models.map((m) => [modelKey(m.model), m.vessels]));
    return Object.keys(planned)
      .filter((k) => (planned[k] ?? 0) > 0)
      .map((k) => { const key = modelKey(k); const goal = planned[k] ?? 0; const has = have.get(key) ?? 0; return { model: key, goal, has, need: Math.max(0, goal - has) }; })
      .sort((a, b) => rankModel(a.model) - rankModel(b.model) || a.model.localeCompare(b.model));
  }, [data]);
  const goal = rows.reduce((a, r) => a + r.goal, 0);
  const has = rows.reduce((a, r) => a + r.has, 0);
  const need = rows.reduce((a, r) => a + r.need, 0);
  const sub = rows.length ? `${has} of ${goal} boats · ${need ? `${need} still needed` : 'all built'}` : undefined;
  return (
    <Panel id="dash-buildplan" className="dash-wide" title="Build plan" sub={sub} to="/reporting" linkText="Reporting">
      {!data ? <Pending isError={isError} /> : rows.length === 0 ? <Empty>No build plan set. Enter the planned boats per model in Assembly Reporting.</Empty> : (
        <div className="dash-models" style={modelGrid(rows.length)}>
          {rows.map((r) => (
            <ModelBar key={r.model} model={r.model} pct={r.goal ? (r.has / r.goal) * 100 : 0} value={`${r.has}/${r.goal}`}
              title={`${r.model}: ${r.has} of ${r.goal} planned${r.need ? `, ${r.need} still needed` : ', done'}`} />
          ))}
        </div>
      )}
    </Panel>
  );
}

/** "synced 08:42": when this browser last loaded the fleet (the Sync button refreshes it). */
function SyncedAt() {
  const { dataUpdatedAt } = useOverview();
  const df = useDateFormatter();
  if (!dataUpdatedAt) return null;
  return <> · synced {df.time(dataUpdatedAt)}</>;
}

// ── page ──────────────────────────────────────────────────────────────────────
// Which boxes appear is admin-controlled (Administration → Dashboard), delivered per user on /me.
// Each box is still gated by the viewer's own permission, so enabling a box never leaks data.
// The "glance" box is the fact sentence and "fleet" is "Where the fleet is". Desktop reads the fleet,
// then the action lists as a 2x2 (Overdue | This week, On hold | Upcoming), the statistics, then the
// other lists two-up. A narrow phone stacks one column, so it leads with the lists people act on and
// puts the fleet and the per-model statistics last.
const DESKTOP_LISTS = ['overdue', 'agenda', 'holds', 'deliveries'];
const DESKTOP_STATS = ['completion', 'buildplan'];
const DESKTOP_MORE = ['orders', 'servicereq', 'notif_by_dept', 'notifications'];
const PHONE_ORDER = ['overdue', 'holds', 'agenda', 'deliveries', 'orders', 'servicereq', 'notifications', 'notif_by_dept', 'fleet', 'completion', 'buildplan'];
const OVERVIEW_BOXES = ['fleet', 'overdue', 'holds', 'deliveries', 'completion'];

/** where each `tail` panel goes (0 left, 1 right) so the two columns end as close together as they can:
 *  every placement is tried against the measured heights (two statistics at most, so four tries), and
 *  the current one is kept unless another ends the columns 40px closer. A panel changes height when it
 *  moves (the columns differ in width), so moves are capped: once the lists' own heights settle, at
 *  most three moves are made and never straight back to the placement just left, so it always settles. */
function useBalancedTails(n: number, active: boolean) {
  const left = useRef<HTMLDivElement>(null);
  const right = useRef<HTMLDivElement>(null);
  const tails = useRef<(HTMLDivElement | null)[]>([]);
  const memo = useRef({ base: '', prev: '', moves: 0 });
  const [sides, setSides] = useState<number[]>([]);
  const key = sides.join();
  useLayoutEffect(() => {
    const a = left.current, b = right.current;
    if (!active || !a || !b || typeof ResizeObserver === 'undefined') return;
    const els = tails.current.slice(0, n);
    const check = () => {
      const zeros = Array<number>(n).fill(0);
      // one-column page: the columns dissolve (display: contents), so keep the statistics in order
      if (a.getClientRects().length === 0) { setSides((cur) => (cur.join() === zeros.join() ? cur : zeros)); return; }
      const base = `${a.offsetHeight},${b.offsetHeight}`;
      const m = memo.current;
      if (base !== m.base) { m.base = base; m.prev = ''; m.moves = 0; }
      if (m.moves >= 3) return;
      const th = els.map((t) => t?.offsetHeight ?? 0);
      const gap = (s: number[]) => {
        let l = a.offsetHeight, r = b.offsetHeight;
        s.forEach((side, i) => { if (side) r += th[i] ?? 0; else l += th[i] ?? 0; });
        return Math.abs(l - r);
      };
      setSides((cur) => {
        const now = cur.length === n ? cur : zeros;
        let best = now, bestGap = gap(now) - 40;
        for (let k = 0; k < 1 << n; k++) {
          const s = Array.from({ length: n }, (_, i) => (k >> i) & 1);
          if (s.join() === m.prev) continue;
          const g = gap(s);
          if (g < bestGap) { best = s; bestGap = g; }
        }
        if (best.join() === cur.join()) return cur;
        m.prev = cur.join(); m.moves += 1;
        return best;
      });
    };
    check();
    const ro = new ResizeObserver(check);
    [a, b, ...els].forEach((el) => el && ro.observe(el));
    return () => ro.disconnect();
  }, [active, n, key]);
  return { left, right, tails, sides };
}

/** a two-up group as two independent columns (1st, 3rd… left; 2nd, 4th… right), so a short panel never
 *  leaves a blank band beside a long one. The `tail` panels (the statistics) then go under whichever
 *  column balances the two best; on a one-column page they fall in after every list. A lone panel (the
 *  others switched off or not permitted) takes the whole width. */
function Duo({ panels, tail = [] }: { panels: ReactNode[]; tail?: ReactNode[] }) {
  const { left, right, tails, sides } = useBalancedTails(tail.length, panels.length >= 2);
  if (panels.length + tail.length === 0) return null;
  if (panels.length < 2) return <div className="dash-duo is-one">{panels}{tail}</div>;
  const under = (side: number) => tail.map((t, i) => (sides[i] ?? 0) === side
    ? <div key={i} className="dash-tail" ref={(el) => { tails.current[i] = el; }}>{t}</div>
    : null);
  return (
    <div className="dash-duo">
      <div className="dash-col">
        <div className="dash-stack" ref={left}>{panels.filter((_, i) => i % 2 === 0)}</div>
        {under(0)}
      </div>
      <div className="dash-col">
        <div className="dash-stack" ref={right}>{panels.filter((_, i) => i % 2 === 1)}</div>
        {under(1)}
      </div>
    </div>
  );
}

/** desktop: the wide fleet, the action lists two-up (Overdue over On hold | This week over Upcoming)
 *  with the statistics under whichever column ends first, then the remaining lists two-up. */
function DesktopLayout({ pick }: { pick: (ids: string[]) => ReactNode[] }) {
  return (
    <>
      {pick(['fleet'])}
      <Duo panels={pick(DESKTOP_LISTS)} tail={pick(DESKTOP_STATS)} />
      <Duo panels={pick(DESKTOP_MORE)} />
    </>
  );
}

export function Dashboard() {
  const { me, can, canSeePath } = useAuth();
  const { activeSeason } = useSeason();
  const df = useDateFormatter();
  const qc = useQueryClient();
  const narrow = useMediaQuery(MQ.narrow);
  // a local flag, not useIsFetching: the notification/collab polls would make the button flicker.
  const [syncing, setSyncing] = useState(false);

  const enabled = useMemo(() => new Set(me?.dashboardBoxes ?? DEFAULT_DASHBOARD_BOXES), [me?.dashboardBoxes]);
  const show = (id: string, perm?: Permission) => enabled.has(id) && (!perm || can(perm));

  const boxes: Record<string, ReactNode> = {};
  if (show('fleet', 'overview:read')) boxes.fleet = <FleetStages key="fleet" />;
  if (show('overdue', 'overview:read')) boxes.overdue = <OverdueDeliveries key="overdue" />;
  if (show('holds', 'overview:read')) boxes.holds = <BoatsOnHold key="holds" />;
  if (show('agenda', 'agenda:read')) boxes.agenda = <WeekAgenda key="agenda" />;
  if (show('deliveries', 'overview:read')) boxes.deliveries = <UpcomingDeliveries key="deliveries" withOverdue={!boxes.overdue} />;
  if (show('orders', 'sales:read')) boxes.orders = <MyOrders key="orders" email={me?.user.email ?? ''} />;
  if (show('servicereq', 'servicereq:read')) boxes.servicereq = <OpenServiceRequests key="servicereq" />;
  if (show('notif_by_dept', 'management:view')) boxes.notif_by_dept = <NotificationsByDept key="notif-by-dept" />;
  if (show('notifications')) boxes.notifications = <NotificationsCard key="notifications" />;
  if (show('completion', 'overview:read')) boxes.completion = <CompletionByModel key="completion-by-model" />;
  if (show('buildplan', 'report:read')) boxes.buildplan = <BuildPlan key="buildplan" />;
  const pick = (ids: string[]) => ids.filter((id) => boxes[id]).map((id) => boxes[id]);
  const cards = pick(PHONE_ORDER);

  const glance = show('glance', 'overview:read');
  const nothing = !glance && cards.length === 0;
  // only read the fleet's sync time when this page loads the fleet anyway (never an extra request)
  const loadsFleet = glance || OVERVIEW_BOXES.some((id) => boxes[id]);
  const targets: FactTargets = {
    overdue: boxes.overdue ? 'dash-overdue' : undefined,
    holds: boxes.holds ? 'dash-holds' : undefined,
    deliveries: boxes.deliveries ? 'dash-deliveries' : undefined,
    completion: boxes.completion ? 'dash-completion' : undefined,
    boats: canSeePath('/overview') ? '/overview' : undefined,
  };

  const todayLong = df.date(new Date(), { weekday: 'long' });
  const meta = <>{todayLong}{activeSeason && ` · season ${activeSeason}`}{loadsFleet && <SyncedAt />}</>;

  return (
    <>
      <PageHeader title="Dashboard" meta={meta}>
        <button
          className={`btn dash-sync${syncing ? ' is-busy' : ''}`}
          disabled={syncing}
          onClick={async () => { setSyncing(true); try { await qc.invalidateQueries(); } finally { setSyncing(false); } }}
        >{syncing ? <LogoSpin size={15} decorative /> : <IconRefresh size={15} />}<span className="dash-sync-l">{syncing ? 'Syncing…' : 'Sync'}</span></button>
      </PageHeader>

      {!nothing && (
        <div className="dash">
          <div className="dash-grid">
            {glance && <SeasonFacts season={activeSeason} targets={targets} />}
            {narrow ? cards : <DesktopLayout pick={pick} />}
          </div>
        </div>
      )}

      {nothing && <EmptyState title="Your dashboard is empty" hint="No boxes are enabled for you yet. An administrator can add some under Administration → Dashboard." />}
    </>
  );
}
